import React, { useState, useEffect } from "react";
import { useDebugMode } from "../../shared/hooks";
import { DescriptionFeature } from "./Description";
import { TitleFeature } from "./Title";
import { SEOFeature } from "./SEOFeature";
import { AltTextFeature } from "./AltText";
import { ProductTagsFeature } from "./ProductTags";
import ConfigDebugView from "./ConfigDebugView"; // Adjust the import path as needed
import ImageFinderFeature from "./ImageFinder";
import { setJobFilter } from "../../store/filtersSlice";
import { useTranslation } from "../../i18n/hooks";
import {
  Globe,
  Tag,
  Type,
  FileText,
  Image,
  Database,
  X,
  Info,
  Settings2,
  CheckCircle,
  ChevronDown,
  Loader2,
  Search,
  BarChart,
  ArrowLeft,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

import languages from "./languages";
import { EnrichmentConfig } from "../../types/api";
import { validateConfig } from "./validation";
import { api } from "../../services/api";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import {
  useQuery,
  getEnrichments,
  getUserSubscriptions,
  getAppConfig,
} from "wasp/client/operations";
import { useStartEnrichment } from "../../hooks/useStartEnrichment";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setEnrichmentDialogOpen } from "../../store/flowSlice";
import { cn } from "../../client/cn";
import {
  altTextStorageKey,
  CREDIT_WEIGHTS,
  descriptionStorageKey,
  seoStorageKey,
  tagsStorageKey,
  titleStorageKey,
  writingStyles,
} from "./config";
import PreferenceItem from "./PreferenceItem";
import { getPromptFromCache } from "./utils";
import { GeneralSettingsSummary, GeneralSettings } from "./GeneralSettings";
import SectionHeader from "./SectionHeader";
import KeywordPill from "./KeywordPill";
import FormatFeature from "./formatFeature";
import EnhancementButton from "./EnhancementButton";

const EnrichmentConfigView = () => {
  const isDebugMode = useDebugMode();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<"config" | "confirmation">(
    "config"
  );
  const featureSelectedStyles = "order-first"; // Using Tailwind's utility class instead of custom CSS
  const [expanded, setExpanded] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [includedKeywords, setIncludedKeywords] = useState<string[]>([]);
  const [excludedKeywords, setExcludedKeywords] = useState<string[]>([]);
  const [seoPrompt, setSeoPrompt] = useState("custom");
  const [imagefinderGeo, setImagefinderGeo] = useState(""); // Add this state
  const [writingStyleOptions, setWritingStyleOptions] = useState(writingStyles);
  const [seoCustomPrompt, setSeoCustomPrompt] = useState(
    getPromptFromCache(seoStorageKey)
  );
  const [titlePrompt, setTitlePrompt] = useState("custom");
  const [titleCustomPrompt, setTitleCustomPrompt] = useState(
    getPromptFromCache(titleStorageKey)
  );
  const [titleIncludeVariants, setTitleIncludeVariants] = useState(false);
  const [descriptionPrompt, setDescriptionPrompt] = useState("custom");
  const [descriptionKeepOriginal, setDescriptionKeepOriginal] = useState(false);
  const [descriptionIncludeTitle, setDescriptionIncludeTitle] = useState(false);
  const [descriptionRemoveImages, setDescriptionRemoveImages] = useState(false);
  const [descriptionCustomPrompt, setDescriptionCustomPrompt] = useState(
    getPromptFromCache(descriptionStorageKey)
  );
  const [altTextPrompt, setAltTextPrompt] = useState("custom");
  const [altTextCustomPrompt, setAltTextCustomPrompt] = useState(
    getPromptFromCache(altTextStorageKey)
  );
  const [language, setLanguage] = useState("English");
  const [languageOptions, setLanguageOptions] = useState(languages);
  const [writingStyle, setWritingStyle] = useState(localStorage.getItem('writingStyle') || "casual");
  const [categoryPrompt, setCategoryPrompt] = useState("custom");
  const [categoryCustomPrompt, setCategoryCustomPrompt] = useState("");
  const [tagsPrompt, setTagsPrompt] = useState("custom");
  const [tagsCustomPrompt, setTagsCustomPrompt] = useState(
    getPromptFromCache(tagsStorageKey)
  );
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<any>();
  const [shouldSelectImageRadarRegion, setShouldSelectImageRadarRegion] =
    useState(false);

  const { startEnrichment, error, status } = useStartEnrichment({
    onJobStarted: () => {
      dispatch(
        setEnrichmentDialogOpen({
          isOpen: false,
        })
      );
      dispatch(setJobFilter("running"));
      navigate("/admin/jobs");
    },
  });
  const [errors, setErrors] = useState<
    {
      field: string;
      message: string;
    }[]
  >([]);
  const user = useSelector((state: RootState) => state.user);
  const { currentUser, currentCollection } = useSelector(
    (state: RootState) => state.user
  );
  const [selectedFormat, setSelectedFormat] = useState(
    currentCollection?.collection?.miqImportMethod === "excel"
      ? "amazon"
      : "shopify"
  );

  const { enrichedProducts, enrichmentSelectedFeatures } = useSelector(
    (state: RootState) => state.flow
  );
  const [enrichedProductCount, setEnrichedProductCount] = useState<
    null | number
  >(null);
  const { qualityGroup, searchKeyword, date, categoryHierarchy, categoryName, productType } = useSelector(
    (state: RootState) => state.filters
  );
  const filters = useSelector((state: RootState) => state.filters);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        if (!currentCollection.collection) {
          throw new Error("Active collection not found");
        }
        if (!currentCollection.collection?.tableId) {
          throw new Error("Table ID not found");
        }
        const response = await api.getProductList({
          accountId: currentUser?.email!,
          collectionName: currentCollection.collection?.name,
          limit: 10,
          offset: 0,
          sortField: "created_at",
          tableId: currentCollection.collection?.tableId,
          filters: {
            quality_group:
              enrichedProducts === "filtersOnly"
                ? qualityGroup
                : "all_products",
            tags: [],
            search: enrichedProducts === "filtersOnly" ? searchKeyword : "",
            date_range: enrichedProducts === "filtersOnly" ? date : undefined,
            category_hierarchy: categoryHierarchy,
            category_name: categoryName,
            product_type: productType,
          },
          metaFields: [],
        });
        if (!response.data?.content_quality_overview) {
          throw new Error("Content quality overview not found");
        }
        setEnrichedProductCount(
          response.data?.content_quality_overview?.filtered_product_count
        );
      } catch (err) {
        console.error(err);
      }
    };

    if (Array.isArray(enrichedProducts)) {
      setEnrichedProductCount(enrichedProducts.length);
    } else {
      fetchProducts();
    }
  }, [enrichedProducts]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!currentCollection.collection?.tableId) {
        alert("Table id not found");
        return;
      }
      const { data, error, success } = await api.getAssistantSuggestions({
        tableId: currentCollection.collection?.tableId,
        productIds: Array.isArray(enrichedProducts)
          ? enrichedProducts
          : undefined,
        filters:
          enrichedProducts === "filtersOnly"
            ? {
                quality_group: filters.qualityGroup,
                tags: filters.selectedTags,
                search: filters.searchKeyword,
                date_range: filters.date,
                category_hierarchy: filters.categoryHierarchy,
                category_name: filters.categoryName,
                product_type: filters.productType,
              }
            : undefined,
      });

      if (error) {
        console.log(error);
      }
      setSuggestions(data?.content_style_configs);
      if (data?.general_settings.language) {
        const newLanguage =
          data?.general_settings.language.charAt(0).toUpperCase() +
          data?.general_settings.language.slice(1);
        setLanguageOptions([
          {
            value: newLanguage,
            label: newLanguage,
          },
          ...languages.filter((lang) => lang.value !== newLanguage),
        ]);
        setLanguage(newLanguage);
      }
      if (data?.general_settings?.writing_style) {
        const writingStyleCache = localStorage.getItem('writingStyle');
        if(!writingStyleCache){
          const newStyle =
          data?.general_settings?.writing_style?.charAt(0).toUpperCase() +
          data?.general_settings?.writing_style?.slice(1);
        setWritingStyleOptions([
          newStyle,
          ...writingStyles.filter((style) => style !== newStyle),
        ]);
        setWritingStyle(newStyle);
        }
      }
      // console.log(data?.content_style_configs)
    };
    fetchSuggestions();
  }, [enrichedProducts, filters]);

  const { data: enrichments, isLoading: enrichmentsLoading } =
    useQuery(getEnrichments);

  const { data: subscriptions, isLoading: subsLoading } =
    useQuery(getUserSubscriptions);

  const [visibleSections, setVisibleSections] = useState<Record<string, any>>({
    general: false, // General settings visibility
    title: false,
    description: false,
    altText: false,
    marketRadar: false,
    metaFields: false,
    seo: false, // Default to selected like others
    categoryPlacement: false, // New
    productTags: false,
    image_finder: true, // New
  });

  const [selectedFeatures, setSelectedFeatures] = useState<
    Record<string, boolean>
  >(
    enrichmentSelectedFeatures != null
      ? enrichmentSelectedFeatures.reduce(
          (acc, feature) => ({ ...acc, [feature]: true }),
          {}
        )
      : {
          title: true,
          description: true,
          altText: true,
          seo: true,
          image_finder: true,
          //    metaFields: true,
          marketRadar: true,
          // categoryPlacement: true, // New
          productTags: true, // New
        }
  );

  useEffect(() => {
    if (selectedFeatures.image_finder && imagefinderGeo === "") {
      setShouldSelectImageRadarRegion(true);
    } else {
      setShouldSelectImageRadarRegion(false);
    }
  }, [selectedFeatures.image_finder, imagefinderGeo]);

  const toggleSectionVisibility = (section: string) => {
    setVisibleSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    // If we're opening the section, scroll it into view
    if (!visibleSections[section]) {
      // Wait for the state update and DOM changes to complete
      setTimeout(() => {
        const cardElement = document.getElementById(`${section}-card`);
        if (cardElement) {
          const headerOffset = 100; // Adjust this value based on your header height
          const elementPosition = cardElement.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 50); // Reduced timeout for better responsiveness
    }
  };

  const getSelectedFeatureCount = () => {
    let count = 0;
    for (const key in selectedFeatures) {
      if (selectedFeatures[key]) count++;
    }
    return count;
  };

  const isMaintanceMode = async () => {
    const res = await getAppConfig();
    return res.miqServiceMaintenanceMode;
  };

  const [isMaintenance, setIsMaintenance] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(false);

  useEffect(() => {
    const checkMaintenance = async () => {
      setCheckingMaintenance(true);
      const res = await isMaintanceMode();
      setIsMaintenance(res);
      setCheckingMaintenance(false);
      return res;
    };

    if (currentStep === "confirmation") {
      checkMaintenance();

      let interval: NodeJS.Timeout | null = null;

      // Set up polling if in maintenance mode
      const setupPolling = async () => {
        const isInMaintenance = await checkMaintenance();
        if (true) {
          interval = setInterval(checkMaintenance, 5000);
        }
      };

      setupPolling();

      // Cleanup interval on unmount or step change
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [currentStep]);

  const handleSave = () => {
    setShowSavedMessage(true);
    setTimeout(() => setShowSavedMessage(false), 3000);
  };

  const addKeyword = (keyword: string, type: "included" | "excluded") => {
    if (!keyword.trim()) return;

    if (type === "included") {
      setIncludedKeywords((prev) => [...new Set([...prev, keyword.trim()])]);
    } else {
      setExcludedKeywords((prev) => [...new Set([...prev, keyword.trim()])]);
    }
  };

  const toggleFeatureSelection = (feature: string, forceState?: boolean) => {
    const newState =
      forceState !== undefined ? forceState : !selectedFeatures[feature];

    // Update both selected features and visibility together
    setSelectedFeatures((prev) => {
      const updated = {
        ...prev,
        [feature]: newState,
      };

      // Calculate new credits only if we have product count
      if (enrichedProductCount !== null) {
        const newTotalCredits = calculateTotalCredits(
          updated,
          enrichedProductCount
        );
        setTotalCredits(newTotalCredits);
      }

      return updated;
    });
  };

  const calculateTotalCredits = (
    features: Record<string, boolean>,
    productCount: number = 10
  ) => {
    // console.log('Calculating credits with:', { features, productCount }); // Debug log

    try {
      // Calculate credits per product
      const creditsPerProduct = Object.entries(features).reduce(
        (total, [feature, isSelected]) => {
          if (isSelected && CREDIT_WEIGHTS[feature]) {
            const credits = CREDIT_WEIGHTS[feature];
            // console.log(`Feature ${feature} adds ${credits} credits`); // Debug log
            return total + credits;
          }
          return total;
        },
        0
      );

      const total = creditsPerProduct * (productCount || 10);
      // console.log(`Final calculation: ${creditsPerProduct} × ${productCount} = ${total}`); // Debug log
      return total;
    } catch (error) {
      // console.error('Error calculating credits:', error);
      return 0;
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    type: "included" | "excluded"
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.target as HTMLInputElement;
      addKeyword(input.value, type);
      input.value = ""; // Clear input after adding
    }
  };

  const toggleSection = (section: string) => {
    setVisibleSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const handleVisibilityChange = (feature: string, visible: boolean) => {
    // Only allow visibility changes if feature is selected
    if (selectedFeatures[feature]) {
      setVisibleSections((prev) => ({
        ...prev,
        [feature]: visible,
      }));
    }
  };

  const [marketRadarGeo, setMarketRadarGeo] = useState("us"); // Default to USA

  const handleStartEnhancement = async () => {
    setErrors([]);

    const config: EnrichmentConfig = {
      general: {
        exclude_keywords: excludedKeywords,
        include_keywords: includedKeywords,
        language: language,
        writing_style: writingStyle,
        format: selectedFormat,
      },

      image_finder: {
        generate: selectedFeatures.image_finder ?? false,
        geo: imagefinderGeo,
      },

      title: {
        generate: selectedFeatures.title ?? false,
        include_variant_info: titleIncludeVariants,
        prompt: titlePrompt === "custom" ? titleCustomPrompt : titlePrompt,
      },

      description: {
        generate: selectedFeatures.description ?? false,
        keep_original: descriptionKeepOriginal,
        include_product_title: descriptionIncludeTitle,
        format: selectedFormat,
        prompt:
          descriptionPrompt === "custom"
            ? descriptionCustomPrompt
            : descriptionPrompt,
      },

      description_bullets: {
        generate: false,
        prompt: "",
        format: "bullet",
        keep_original: true,
      },

      alt_text: {
        generate: selectedFeatures.altText ?? false,
        prompt:
          altTextPrompt === "custom" ? altTextCustomPrompt : altTextPrompt,
      },

      seo: {
        generate: selectedFeatures.seo ?? false,
        prompt: seoPrompt === "custom" ? seoCustomPrompt : seoPrompt,
      },

      market_radar: {
        generate: selectedFeatures.marketRadar ?? false,
        geo: marketRadarGeo,
      },

      product_tags: {
        generate: selectedFeatures.productTags ?? false,
        prompt: tagsPrompt === "custom" ? tagsCustomPrompt : tagsPrompt,
      },
    };
    const { valid, errors } = validateConfig(config);
    console.log(errors);
    if (!valid) {
      setErrors(errors);
      return;
    }
    try {
      if (Array.isArray(enrichedProducts)) {
        await startEnrichment({
          config,
          usage: totalCredits,
          productIds: enrichedProducts,
          productCount: enrichedProducts.length,
        });
      } else if (enrichedProducts === "filtersOnly") {
        await startEnrichment({
          config,
          usage: totalCredits,
          filters: {
            quality_group: filters.qualityGroup,
            tags: filters.selectedTags,
            search: filters.searchKeyword,
            date_range: filters.date,
            category_hierarchy: filters.categoryHierarchy,
            category_name: filters.categoryName,
            product_type: filters.productType,
          },
          productCount: enrichedProductCount ?? undefined,
        });
      } else {
        await startEnrichment({
          config,
          usage: totalCredits,
          productCount: enrichedProductCount ?? undefined,
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const featureCards = [
    {
      id: "title",
      title: t('enrichmentConfig.feature_title'),
      component: (
        <TitleFeature
          isVisible={visibleSections.title}
          isSelected={selectedFeatures.title}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("title", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("title", selected)
          }
          suggestions={suggestions}
          errors={errors}
          prompt={titlePrompt}
          customPrompt={titleCustomPrompt}
          onPromptChange={setTitlePrompt}
          onCustomPromptChange={setTitleCustomPrompt}
          titleIncludeVariants={titleIncludeVariants}
          setTitleIncludeVariants={setTitleIncludeVariants}
        />
      ),
    },
    {
      id: "description",
      title: t('enrichmentConfig.feature_description'),
      component: (
        <DescriptionFeature
          isVisible={visibleSections.description}
          isSelected={selectedFeatures.description}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("description", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("description", selected)
          }
          suggestions={suggestions}
          errors={errors}
          prompt={descriptionPrompt}
          customPrompt={descriptionCustomPrompt}
          onPromptChange={setDescriptionPrompt}
          onCustomPromptChange={setDescriptionCustomPrompt}
          keepOriginal={descriptionKeepOriginal}
          onKeepOriginalChange={setDescriptionKeepOriginal}
          includeTitle={descriptionIncludeTitle}
          onIncludeTitleChange={setDescriptionIncludeTitle}
        />
      ),
    },

    {
      id: "seo",
      title: t('enrichmentConfig.feature_seo'),
      component: (
        <SEOFeature
          isVisible={visibleSections.seo}
          isSelected={selectedFeatures.seo}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("seo", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("seo", selected)
          }
          errors={errors}
          prompt={seoPrompt}
          customPrompt={seoCustomPrompt}
          onPromptChange={setSeoPrompt}
          onCustomPromptChange={setSeoCustomPrompt}
        />
      ),
    },

    {
      id: "altText",
      title: t('enrichmentConfig.feature_alt_text'),
      component: (
        <AltTextFeature
          isVisible={visibleSections.altText}
          isSelected={selectedFeatures.altText}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("altText", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("altText", selected)
          }
          errors={errors}
          prompt={altTextPrompt}
          customPrompt={altTextCustomPrompt}
          onPromptChange={setAltTextPrompt}
          onCustomPromptChange={setAltTextCustomPrompt}
        />
      ),
    },

    {
      id: "productTags",
      title: t('enrichmentConfig.feature_product_tags'),
      component: (
        <ProductTagsFeature
          isVisible={visibleSections.productTags}
          isSelected={selectedFeatures.productTags}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("productTags", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("productTags", selected)
          }
          errors={errors}
          prompt={tagsPrompt}
          customPrompt={tagsCustomPrompt}
          onPromptChange={setTagsPrompt}
          onCustomPromptChange={setTagsCustomPrompt}
        />
      ),
    },

    {
      id: "image_finder",
      title: t('enrichmentConfig.feature_image_finder'),
      component: (
        <ImageFinderFeature
          isVisible={visibleSections.image_finder}
          isSelected={selectedFeatures.image_finder}
          onVisibilityChange={(visible) =>
            handleVisibilityChange("image_finder", visible)
          }
          onSelectionChange={(selected) =>
            toggleFeatureSelection("image_finder", selected)
          }
          geo={imagefinderGeo}
          onGeoChange={setImagefinderGeo}
        />
      ),
    },
  ];

  const JobConfirmationView = () => (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentStep("config")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <CardTitle className="text-2xl font-semibold">
                {t('enrichmentConfig.review_enhancement_job')}
              </CardTitle>
              <CardDescription className="text-gray-500">
                {t('enrichmentConfig.review_description')}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Selected Features Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {t('enrichmentConfig.selected_enhancements')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
              <Database className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">{t('enrichmentConfig.marketplace_format')}</p>
                <p className="font-medium text-gray-900 capitalize">
                  {selectedFormat}
                </p>
              </div>
            </div>
            {Object.entries(selectedFeatures)
              .filter(([_, isSelected]) => isSelected)
              .map(([feature]) => (
                <div
                  key={feature}
                  className="flex items-center gap-3 bg-green-50 p-3 rounded-lg"
                >
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">
                    {feature
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Language and Style Settings */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {t('enrichmentConfig.content_settings')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
              <Globe className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">{t('enrichmentConfig.language')}</p>
                <p className="font-medium text-gray-900">{language}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
              <Type className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">{t('enrichmentConfig.writing_style')}</p>
                <p className="font-medium text-gray-900 capitalize">
                  {writingStyle}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Summary */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900">
                {t('enrichmentConfig.products_ready')}
              </p>
              <p className="text-sm text-blue-700">
                {enrichedProductCount === 1
                  ? t('enrichmentConfig.one_product_selected')
                  : t('enrichmentConfig.multiple_products_selected', { count: enrichedProductCount?.toLocaleString() })}{" "}
                {t('enrichmentConfig.for_enhancement')}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const handleFeatureSelection = () => {
    setCurrentStep("confirmation");
  };

  const tempValueHolder = 15;
  const [totalCredits, setTotalCredits] = useState(0);

  useEffect(() => {
    if (enrichedProductCount === null) return;
    // Calculate initial credits
    const initialTotal = calculateTotalCredits(
      selectedFeatures,
      enrichedProductCount
    );
    setTotalCredits(initialTotal);
  }, [enrichedProductCount, selectedFeatures]);

  const sync = (key: string): string => {
    const mappings: Record<string, string> = {
      marketRadar: "market_radar",
      altText: "alt_text",
      metaFields: "meta_fields",
      title: "title",
      description: "description",
      seo: "seo_content", // Add mapping for SEO
    };

    return mappings[key] || key;
  };

  useEffect(() => {
    if (enrichedProductCount === null) return;
    const newTotal = calculateTotalCredits(
      selectedFeatures,
      enrichedProductCount
    );
    setTotalCredits(newTotal);
  }, [selectedFeatures, enrichedProductCount]);

  useEffect(() => {}, [enrichments, selectedFeatures]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1  overflow-y-auto">
        <div className="space-y-8 p-3 py-4 mt-8 md:mt-0 md:px-8 md:py-6 md:pb-[90px]">
          {/* Header */}
          <div className="bg-white sticky top-0 z-10 pb-4">
            <div className="flex flex-col md:flex-row items-center justify-between mb-4"></div>
            {/* General Settings */}
            {currentStep === "config" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader
                    className="cursor-pointer"
                    onClick={() => toggleSection("general")}
                  >
                    <div className="flex items-center justify-between">
                      <GeneralSettingsSummary
                        visibleSections={visibleSections}
                        languages={languages}
                        language={language}
                        writingStyle={writingStyle}
                        includedKeywords={includedKeywords}
                        excludedKeywords={excludedKeywords}
                      />
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          visibleSections.general ? "transform rotate-180" : ""
                        }`}
                      />
                    </div>
                  </CardHeader>
                  {visibleSections.general && (
                    <GeneralSettings
                      language={language}
                      setLanguage={setLanguage}
                      languageOptions={languageOptions}
                      writingStyle={writingStyle}
                      setWritingStyle={setWritingStyle}
                      includedKeywords={includedKeywords}
                      setIncludedKeywords={setIncludedKeywords}
                      excludedKeywords={excludedKeywords}
                      setExcludedKeywords={setExcludedKeywords}
                      errors={errors}
                      writingStyleOptions={writingStyleOptions}
                      handleKeyPress={handleKeyPress}
                      addKeyword={addKeyword}
                    />
                  )}
                </Card>
                {user.currentCollection?.collection?.miqImportMethod ===
                  "excel" && (
                  <FormatFeature
                    selectedFormat={selectedFormat}
                    onFormatChange={setSelectedFormat}
                  />
                )}
              </div>
            )}

            <div className="space-y-8 mt-8">
              {isDebugMode && (
                <ConfigDebugView
                  excludedKeywords={excludedKeywords}
                  includedKeywords={includedKeywords}
                  language={language}
                  writingStyle={writingStyle}
                  selectedFeatures={selectedFeatures}
                  titlePrompt={titlePrompt}
                  titleCustomPrompt={titleCustomPrompt}
                  titleIncludeVariants={titleIncludeVariants}
                  descriptionPrompt={descriptionPrompt}
                  descriptionCustomPrompt={descriptionCustomPrompt}
                  descriptionKeepOriginal={descriptionKeepOriginal}
                  descriptionIncludeTitle={descriptionIncludeTitle}
                  altTextPrompt={altTextPrompt}
                  altTextCustomPrompt={altTextCustomPrompt}
                  seoPrompt={seoPrompt}
                  seoCustomPrompt={seoCustomPrompt}
                  tagsPrompt={tagsPrompt}
                  tagsCustomPrompt={tagsCustomPrompt}
                  marketRadarGeo={marketRadarGeo}
                  imagefinderGeo={imagefinderGeo}
                  selectedFormat={selectedFormat}
                />
              )}
            </div>

            <div className="space-y-4">
              {currentStep === "config" ? (
                <>
                  {/* Selection Bar */}
                  <div className="mt-6 mx-[-12px] md:mx-0 border rounded-none md:rounded-lg bg-white mb-4">
                    <div className="p-4 border-b">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Settings2 className="flex-shrink-0 w-5 h-5 text-blue-600" />
                          <h3 className="font-medium">
                            {getSelectedFeatureCount() === 0
                              ? t('enrichmentConfig.no_features_selected')
                              : `${getSelectedFeatureCount()} ${t('enrichmentConfig.features_selected')}`}
                          </h3>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-sm text-blue-600 hover:text-blue-700"
                          onClick={() => {
                            const hasUnselectedFeature = featureCards.some(
                              (feature) => !selectedFeatures[feature.id]
                            );
                            featureCards.forEach((feature) => {
                              toggleFeatureSelection(
                                feature.id,
                                hasUnselectedFeature
                              );
                            });
                          }}
                        >
                          {featureCards.every(
                            (feature) => selectedFeatures[feature.id]
                          )
                            ? t('enrichmentConfig.deselect_all')
                            : t('enrichmentConfig.select_all')}
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {featureCards.map((feature) =>
                          feature.id === "image_finder" ? (
                            <div
                              key={feature.id}
                              className={cn(
                                "p-4 border rounded cursor-pointer",
                                selectedFeatures[feature.id]
                                  ? "bg-blue-50 border-blue-200"
                                  : "hover:bg-gray-50"
                              )}
                              onClick={() => toggleFeatureSelection(feature.id)}
                            >
                              <div className="flex items-center gap-2">
                                <Switch
                                  data-testid="enhancement-switch"
                                  data-switch-type={feature.id}
                                  checked={selectedFeatures[feature.id]}
                                  onCheckedChange={(checked) => {
                                    toggleFeatureSelection(feature.id, checked);
                                    if (!checked) {
                                      handleVisibilityChange(feature.id, false);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">
                                      {feature.title}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t('enrichmentConfig.image_finder_description')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              key={feature.id}
                              className={cn(
                                "p-4 border rounded flex items-center gap-2 cursor-pointer",
                                selectedFeatures[feature.id]
                                  ? "bg-blue-50 border-blue-200"
                                  : "hover:bg-gray-50"
                              )}
                              onClick={() => toggleFeatureSelection(feature.id)}
                            >
                              <Switch
                                data-testid="enhancement-switch"
                                data-switch-type={feature.id}
                                checked={selectedFeatures[feature.id]}
                                onCheckedChange={(checked) => {
                                  toggleFeatureSelection(feature.id, checked);
                                  if (!checked) {
                                    handleVisibilityChange(feature.id, false);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-sm font-medium">
                                {feature.title}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Feature Cards */}
                  {featureCards.map((feature) => (
                    <div
                      key={feature.id}
                      className={selectedFeatures[feature.id] ? "" : "hidden"}
                    >
                      {feature.component}
                    </div>
                  ))}
                </>
              ) : (
                <JobConfirmationView />
              )}
            </div>
          </div>

          <div className="space-y-8 mt-8"></div>

          {/* Footer section */}
        </div>

        <div
          className={cn(
            "sticky bottom-0 border-t bg-white py-4 px-6 mt-auto z-10 shadow-lg",
            {
              "absolute w-full": true,
            }
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-col text-sm md:hidden">
              <span>
                {enrichedProductCount !== null ? (
                  enrichedProductCount
                ) : (
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                )}{" "}
                products
              </span>{" "}
              <span>
                {totalCredits}/
                {subscriptions?.[0]?.creditBalances[0].remainingCredits || 0} 
                {t('enrichmentConfig.total_credits')}
              </span>
            </div>
            <div className="items-center gap-4 hidden md:flex">
              <div>
                <div className="text-sm text-gray-500">{t('enrichmentConfig.job_includes')}</div>
                <div className="font-medium">
                  {enrichedProductCount === null ? (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  ) : enrichedProductCount === 1 ? (
                    `1 ${t('productsView.product_singular')}`
                  ) : (
                    `${enrichedProductCount.toLocaleString()} ${t('productsView.product_plural')}`
                  )}
                </div>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  {t('enrichmentConfig.total_credits_needed')}
                  <div className="relative group">
                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="relative group">
                      <div className="absolute bottom-[100%] right-[50%] translate-x-1/2 mb-2 w-80 p-4 bg-white rounded-lg shadow-lg border text-sm z-[9999] hidden group-hover:block">
                        <p className="font-medium mb-3 text-gray-900">
                          {t('enrichmentConfig.credit_cost_breakdown')}
                        </p>
                        <ul className="space-y-2">
                          {Object.entries(CREDIT_WEIGHTS).map(
                            ([feature, credits]) => {
                              // Map feature names to their display names and icons
                              const featureDisplayInfo = {
                                title: { name: t('enrichmentConfig.feature_title'), icon: Type },
                                description: {
                                  name: t('enrichmentConfig.feature_description'),
                                  icon: FileText,
                                },
                                altText: {
                                  name: t('enrichmentConfig.feature_alt_text'),
                                  icon: Image,
                                },
                                seo: { name: t('enrichmentConfig.feature_seo'), icon: Search },
                                metaFields: {
                                  name: t('enrichmentConfig.feature_meta_fields'),
                                  icon: Database,
                                },
                                marketRadar: {
                                  name: t('enrichmentConfig.feature_market_radar'),
                                  icon: BarChart,
                                },
                                productTags: {
                                  name: t('enrichmentConfig.feature_product_tags'),
                                  icon: Tag,
                                },
                              };

                              const displayInfo = featureDisplayInfo[feature as keyof typeof featureDisplayInfo];
                              if (!displayInfo) return null;

                              return (
                                <li
                                  key={feature}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <displayInfo.icon className="w-4 h-4 text-gray-400" />
                                    <span>{displayInfo.name}</span>
                                  </div>
                                  <span className="text-gray-600">
                                    {credits} {t('enrichmentConfig.credits')}
                                  </span>
                                </li>
                              );
                            }
                          )}
                        </ul>
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center justify-between font-medium">
                            <span>{t('enrichmentConfig.total_credits')}</span>
                            <span>
                              {totalCredits} {t('enrichmentConfig.credits')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="font-medium">{totalCredits} {t('enrichmentConfig.credits')}</div>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <div className="text-sm text-gray-500">{t('enrichmentConfig.your_credit_balance')}</div>
                <div className="font-medium">
                  {subscriptions?.[0]?.creditBalances[0].remainingCredits || 0}{" "}
                  {t('enrichmentConfig.credits')}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              {totalCredits >
                (subscriptions?.[0]?.creditBalances[0].remainingCredits ||
                  0) && (
                <div className="text-sm font-medium text-orange-600">
                  {t('enrichmentConfig.need_more_credits', { count: totalCredits -
                    (subscriptions?.[0]?.creditBalances[0].remainingCredits ||
                      0) })}
                </div>
              )}
              <EnhancementButton
                needsCountrySelection={
                  selectedFeatures.image_finder && !imagefinderGeo
                }
                onCountrySelect={setImagefinderGeo}
                selectedCountry={imagefinderGeo}
                isLoading={
                  status === "in_progress" ||
                  checkingMaintenance ||
                  isMaintenance
                }
                onContinue={() => {
                  if (currentStep === "config") {
                    handleFeatureSelection();
                  } else {
                    handleStartEnhancement();
                  }
                }}
                disabled={
                  getSelectedFeatureCount() === 0 ||
                  checkingMaintenance ||
                  isMaintenance ||
                  totalCredits >
                (subscriptions?.[0]?.creditBalances[0].remainingCredits ||
                  0)
                }
                currentStep={currentStep}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrichmentConfigView;
